import { create } from 'zustand'
import type { AIChatMessage } from '@/services/database'
import type { AgentToolCall } from '@/services/aiService'

interface PendingApproval {
  threadId: string
  interruptValue: unknown
}

// 로컬 전용 확장: 실패한 메시지에 재시도용 원문(payload)을 첨부.
// retryPayload는 UI 어포던스 전용이며 IndexedDB에는 저장하지 않는다.
export interface AIChatUIMessage extends AIChatMessage {
  retryPayload?: string
}

interface AIChatState {
  messages: AIChatUIMessage[]
  isLoading: boolean
  threadId: string | null
  pendingApproval: PendingApproval | null
  lastToolCalls: AgentToolCall[]
  loadHistory: () => Promise<void>
  sendMessage: (content: string) => Promise<void>
  retryMessage: (errorCreatedAt: string, payload: string) => Promise<void>
  approveAction: (approved: boolean) => Promise<void>
  clearHistory: () => Promise<void>
  startNewSession: () => void
}

// 시스템 프롬프트 빌더: 개인 생산성 컨텍스트 주입
async function buildSystemPrompt(): Promise<string> {
  const { useTaskStore } = await import('./taskStore')
  const { useProfileStore } = await import('./profileStore')
  const { useCategoryStore } = await import('./categoryStore')

  const tasks = useTaskStore.getState().tasks
  const profile = useProfileStore.getState().profile
  const stats = useProfileStore.getState().stats
  const categories = useCategoryStore.getState().categories

  const now = new Date()
  const fmtLocal = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
  const today = fmtLocal(now)
  const dayNames = ['일', '월', '화', '수', '목', '금', '토']
  const dayOfWeek = dayNames[now.getDay()]

  const todayTasks = tasks.filter((t) => t.dueDate === today)
  const overdue = tasks.filter((t) => t.status === 'pending' && t.dueDate && t.dueDate < today)
  const pending = tasks.filter((t) => t.status === 'pending')
  const completed = tasks.filter((t) => t.status === 'completed')

  // 우선순위 분포
  const highCount = pending.filter((t) => t.priority === 'high').length
  const mediumCount = pending.filter((t) => t.priority === 'medium').length
  const lowCount = pending.filter((t) => t.priority === 'low').length

  // 미루기 패턴 (rescheduleCount 높은 할일)
  const postponed = pending
    .filter((t) => (t.rescheduleCount || 0) > 0)
    .sort((a, b) => (b.rescheduleCount || 0) - (a.rescheduleCount || 0))
    .slice(0, 5)

  // 최근 7일 완료 통계 — completedAt(UTC ISO)을 사용자 로컬 날짜로 변환 후 비교
  const sevenDaysAgo = fmtLocal(new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000))
  const recentCompleted = completed.filter(
    (t) => !!t.completedAt && fmtLocal(new Date(t.completedAt)) >= sevenDaysAgo,
  ).length

  // 카테고리별 현황
  const categoryStats = categories.map((c) => {
    const catTasks = tasks.filter((t) => t.categoryId === c.id)
    const catCompleted = catTasks.filter((t) => t.status === 'completed').length
    return `${c.name}: ${catCompleted}/${catTasks.length}`
  }).join(', ')

  // 서브태스크 진행도
  const tasksWithSubtasks = pending.filter((t) => t.subtasks?.length > 0).slice(0, 5)
  const subtaskInfo = tasksWithSubtasks.length > 0
    ? tasksWithSubtasks.map((t) => {
        const done = t.subtasks.filter((s) => s.isCompleted).length
        return `${t.title}: ${done}/${t.subtasks.length} 완료`
      }).join(', ')
    : ''

  return `당신은 시니어 프로젝트 매니저 겸 생산성 코치입니다. 전문적이고 통찰력 있는 톤으로 한국어로 대화하세요.

역할:
- 데이터를 분석하여 패턴, 병목, 리스크를 파악하세요.
- 단순 나열이 아닌, 원인 분석 → 우선순위 판단 → 구체적 액션 플랜을 제시하세요.
- 시간 관리, 에너지 배분, 작업 순서 최적화 관점에서 조언하세요.
- "왜" 그렇게 해야 하는지 근거를 함께 설명하세요.

출력 규칙 (반드시 준수):
- 이모지 사용 절대 금지.
- 마크다운 표(| --- |) 절대 사용 금지. 표 대신 서술형 문장이나 목록으로 작성하세요.
- 제목(#, ##) 사용 금지. **굵은글씨**로 대체하세요.
- 허용되는 마크다운: **굵은글씨**, 목록(-, 1.), 줄바꿈만 사용하세요.
- 정보를 전달할 때 표 형식이 아닌 자연스러운 서술형 텍스트로 작성하세요.
- 충분히 상세하게 분석하되, 불필요한 반복은 피하세요.

현재 날짜: ${today} (${dayOfWeek}요일)

사용자 정보:
- 이름: ${profile.name}
- 스트릭: ${stats.currentStreak}일 연속
- 전체: 완료 ${stats.completedCount}개, 미완료 ${stats.pendingCount}개
- 최근 7일 완료: ${recentCompleted}개
- 우선순위 분포: HIGH ${highCount}, MEDIUM ${mediumCount}, LOW ${lowCount}

오늘(${today}) 할일 (${todayTasks.length}개):
${todayTasks.slice(0, 15).map((t) => `- [${t.status === 'completed' ? 'v' : ' '}] ${t.title} (${t.priority})`).join('\n') || '(없음)'}

기한 초과 (${overdue.length}개):
${overdue.slice(0, 10).map((t) => `- ${t.title} (마감: ${t.dueDate})`).join('\n') || '(없음)'}

미완료 TOP 15:
${pending.slice(0, 15).map((t) => `- ${t.title} (${t.priority}${t.dueDate ? ', 마감: ' + t.dueDate : ''})`).join('\n') || '(없음)'}

${postponed.length > 0 ? `자주 미루는 할일:\n${postponed.map((t) => `- ${t.title} (${t.rescheduleCount}회 연기)`).join('\n')}` : ''}

${subtaskInfo ? `서브태스크 진행도: ${subtaskInfo}` : ''}

카테고리별: ${categoryStats || '(없음)'}

사용자의 질문에 데이터 기반으로 분석하고 구체적인 조언을 제공하세요.`
}

export const useAIChatStore = create<AIChatState>()((set, get) => ({
  messages: [],
  isLoading: false,
  threadId: null,
  pendingApproval: null,
  lastToolCalls: [],

  loadHistory: async () => {
    try {
      const { db } = await import('@/services/database')
      const messages = await (db as unknown as { aiChatMessages: import('dexie').Table<AIChatMessage> })
        .aiChatMessages.orderBy('createdAt').toArray()
      set({ messages })
    } catch {
      set({ messages: [] })
    }
  },

  sendMessage: async (content: string) => {
    if (get().isLoading) return

    const now = new Date().toISOString()
    const userMsg: AIChatMessage = { role: 'user', content, createdAt: now }

    // 1. 유저 메시지 추가
    set((s) => ({ messages: [...s.messages, userMsg], isLoading: true, lastToolCalls: [] }))

    try {
      const { db } = await import('@/services/database')
      const dbTable = (db as unknown as { aiChatMessages: import('dexie').Table<AIChatMessage> }).aiChatMessages
      await dbTable.add(userMsg)

      // 2. 프로바이더 + API 키 확인
      const { getAIConfig } = await import('@/services/aiService')
      const { provider, apiKey } = getAIConfig()

      // 3. 시스템 프롬프트 + 슬라이딩 윈도우 (최근 10턴)
      const systemPrompt = await buildSystemPrompt()
      const currentThreadId = get().threadId

      // 4. AI 에이전트 호출 (LangGraph — 도구 사용 + HITL + 메모리)
      const { callAgent } = await import('@/services/aiService')
      let response: string
      let actions: { type: string; title?: string }[] = []
      let toolCalls: AgentToolCall[] = []

      try {
        const agentResult = await callAgent(provider, apiKey, content, currentThreadId || undefined, systemPrompt)
        response = agentResult.response
        actions = agentResult.actions
        toolCalls = agentResult.toolCalls || []

        // thread_id 저장
        if (agentResult.threadId) {
          set({ threadId: agentResult.threadId })
        }

        // HITL 인터럽트 처리
        if (agentResult.interrupted) {
          set({
            pendingApproval: {
              threadId: agentResult.threadId!,
              interruptValue: agentResult.interruptValue,
            },
            lastToolCalls: toolCalls,
            isLoading: false,
          })
          // 인터럽트 메시지 추가
          const interruptMsg: AIChatMessage = {
            role: 'assistant',
            content: '[확인 대기중] 에이전트가 작업 변경을 요청했습니다. 승인 또는 거부해주세요.',
            createdAt: new Date().toISOString(),
          }
          set((s) => ({ messages: [...s.messages, interruptMsg] }))
          await dbTable.add(interruptMsg)
          return
        }
      } catch {
        // 에이전트 실패 시 기존 방식 fallback + 사용자 안내 (P3-3)
        const { callAI } = await import('@/services/aiService')
        const recentMessages = get().messages.slice(-10).map((m) => ({
          role: m.role as 'user' | 'assistant',
          content: m.content,
        }))
        const maxTokens = content.length > 50 ? 2048 : 1024
        const fallbackResponse = await callAI(provider, apiKey, systemPrompt, recentMessages, 'chat', maxTokens)
        response = `*[도구 없이 응답합니다]*\n\n${fallbackResponse}`
      }

      // 5. action 정보만 응답에 추가 (도구 정보는 lastToolCalls로 별도 관리)
      const actionSuffix = actions.length > 0
        ? '\n\n' + actions.map((a) => `> ${a.type === 'created' ? '생성' : '수정'}: ${a.title || ''}`).join('\n')
        : ''

      const aiMsg: AIChatMessage = {
        role: 'assistant',
        content: response + actionSuffix,
        createdAt: new Date().toISOString(),
      }
      set((s) => ({ messages: [...s.messages, aiMsg], isLoading: false, lastToolCalls: toolCalls }))
      await dbTable.add(aiMsg)
    } catch (err) {
      const errBase: AIChatMessage = {
        role: 'assistant',
        content: `오류가 발생했습니다: ${err instanceof Error ? err.message : '알 수 없는 오류'}`,
        createdAt: new Date().toISOString(),
      }
      // UI 상태에는 재시도용 원문(retryPayload)을 첨부한다.
      const errMsg: AIChatUIMessage = { ...errBase, retryPayload: content }
      set((s) => ({ messages: [...s.messages, errMsg], isLoading: false }))
      try {
        // DB에는 retryPayload를 제외한 기본 메시지만 저장한다.
        const { db: errDb } = await import('@/services/database')
        await (errDb as unknown as { aiChatMessages: import('dexie').Table<AIChatMessage> }).aiChatMessages.add(errBase)
      } catch { /* DB 저장 실패 무시 */ }
    }
  },

  retryMessage: async (errorCreatedAt: string, payload: string) => {
    if (get().isLoading) return

    // 1. 실패한 오류 메시지를 UI 상태에서 제거 (재시도용 retryPayload가 있는 것만 대상)
    set((s) => ({
      messages: s.messages.filter(
        (m) => !(m.createdAt === errorCreatedAt && m.retryPayload !== undefined),
      ),
    }))

    // 2. DB에 저장된 동일 오류 메시지도 제거 (createdAt 기준)
    try {
      const { db } = await import('@/services/database')
      const dbTable = (db as unknown as { aiChatMessages: import('dexie').Table<AIChatMessage> }).aiChatMessages
      await dbTable.where('createdAt').equals(errorCreatedAt).delete()
    } catch { /* DB 정리 실패 무시 */ }

    // 3. 원래 입력으로 일반 전송 경로 재호출 (HITL 흐름 그대로 유지)
    await get().sendMessage(payload)
  },

  approveAction: async (approved: boolean) => {
    const { pendingApproval } = get()
    if (!pendingApproval) return

    set({ isLoading: true, pendingApproval: null })

    try {
      const { getAIConfig, resumeAgent } = await import('@/services/aiService')
      const { provider, apiKey } = getAIConfig()

      const result = await resumeAgent(provider, apiKey, pendingApproval.threadId, approved)

      const { db } = await import('@/services/database')
      const dbTable = (db as unknown as { aiChatMessages: import('dexie').Table<AIChatMessage> }).aiChatMessages

      const statusMsg = approved ? '[승인됨]' : '[거부됨]'
      const aiMsg: AIChatMessage = {
        role: 'assistant',
        content: `${statusMsg} ${result.response}`,
        createdAt: new Date().toISOString(),
      }
      set((s) => ({
        messages: [...s.messages, aiMsg],
        isLoading: false,
        lastToolCalls: result.toolCalls || [],
      }))
      await dbTable.add(aiMsg)

      // 작업이 변경되었으면 태스크 스토어 갱신
      if (approved && result.actions.length > 0) {
        const { useTaskStore } = await import('./taskStore')
        await useTaskStore.getState().refreshFromDb()
      }
    } catch (err) {
      const errMsg: AIChatMessage = {
        role: 'assistant',
        content: `승인 처리 중 오류: ${err instanceof Error ? err.message : '알 수 없는 오류'}`,
        createdAt: new Date().toISOString(),
      }
      set((s) => ({ messages: [...s.messages, errMsg], isLoading: false }))
    }
  },

  clearHistory: async () => {
    try {
      const { db } = await import('@/services/database')
      await (db as unknown as { aiChatMessages: import('dexie').Table<AIChatMessage> }).aiChatMessages.clear()
    } catch { /* ignore */ }
    set({ messages: [], threadId: null, pendingApproval: null, lastToolCalls: [] })
  },

  startNewSession: () => {
    set({ threadId: null, pendingApproval: null, lastToolCalls: [], messages: [] })
  },
}))
